/**
 * Toast — Radix Toast wrapper.
 * Success toast optionally accepts an `onUndo` action (10s window).
 */
import * as RadixToast from "@radix-ui/react-toast";
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { cn } from "./utils/cn";

type ToastKind = "success" | "info" | "warn" | "error";

type ToastItem = {
  id: string;
  kind: ToastKind;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  duration?: number;
};

type Ctx = {
  toast: (t: Omit<ToastItem, "id">) => void;
};

const ToastContext = createContext<Ctx | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((t: Omit<ToastItem, "id">) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setItems((prev) => [...prev, { id, ...t }].slice(-3));
  }, []);

  const dismiss = (id: string) => setItems((prev) => prev.filter((t) => t.id !== id));

  return (
    <ToastContext.Provider value={{ toast }}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {items.map((t) => {
          const tone =
            t.kind === "success"
              ? "border-[color:rgba(52,211,153,0.35)]"
              : t.kind === "error"
              ? "border-[color:rgba(244,63,94,0.35)]"
              : t.kind === "warn"
              ? "border-[color:rgba(245,158,11,0.35)]"
              : "border-[var(--nx-line-strong)]";
          return (
            <RadixToast.Root
              key={t.id}
              duration={t.kind === "error" ? Infinity : t.duration ?? 3000}
              onOpenChange={(open) => !open && dismiss(t.id)}
              className={cn(
                "grid grid-cols-[1fr_auto] gap-3 rounded-[12px] border bg-[var(--nx-surface)] px-4 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.55)]",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                tone,
              )}
            >
              <div>
                <RadixToast.Title className="text-sm font-medium text-[var(--nx-text)]">{t.title}</RadixToast.Title>
                {t.description ? (
                  <RadixToast.Description className="mt-0.5 text-xs text-[var(--nx-text-dim)]">
                    {t.description}
                  </RadixToast.Description>
                ) : null}
              </div>
              <div className="flex items-center gap-1">
                {t.action ? (
                  <RadixToast.Action
                    altText={t.action.label}
                    onClick={t.action.onClick}
                    className="rounded-[8px] border border-[var(--nx-line)] px-2 py-1 text-xs text-[var(--nx-text)] hover:border-[var(--nx-line-strong)]"
                  >
                    {t.action.label}
                  </RadixToast.Action>
                ) : null}
                <RadixToast.Close
                  aria-label="Dismiss"
                  className="rounded-[8px] p-1 text-[var(--nx-text-dim)] hover:text-[var(--nx-text)]"
                >
                  ✕
                </RadixToast.Close>
              </div>
            </RadixToast.Root>
          );
        })}
        <RadixToast.Viewport className="fixed right-4 top-4 z-[var(--nx-z-toast)] flex w-[360px] max-w-[94vw] flex-col gap-2 outline-none" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}
